import { BookOpen, Download, CheckCircle2, Circle, ExternalLink, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import { openCourseFile } from '../../lib/api'

interface HomeworkFile {
  id: number
  name: string
  category: string
}

interface HomeworkItem {
  id: number
  title: string
  course: string
  course_id?: number | null
  subject: string | null
  lesson_number: number | null
  files: HomeworkFile[]
  has_files: boolean
  has_scan: boolean
  matched_scan_id?: number | null
}

interface TonightsHomeworkProps {
  studentId: number
  items: HomeworkItem[]
  completed: number
  total: number
}

export function TonightsHomework({ studentId, items, completed, total }: TonightsHomeworkProps) {
  const handleDownload = (e: React.MouseEvent, fileId: number) => {
    e.preventDefault()
    e.stopPropagation()
    openCourseFile(fileId)
  }

  const allDone = total > 0 && completed === total

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide flex items-center gap-2">
          <BookOpen className="w-4 h-4" />
          Tonight's Homework
        </h3>
        {total > 0 && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            allDone
              ? 'bg-green-500/20 text-green-400'
              : 'bg-gray-700 text-gray-400'
          }`}>
            {completed}/{total} done
          </span>
        )}
      </div>

      {items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center justify-between p-2 rounded-lg ${
                item.has_scan
                  ? 'bg-green-900/20 border border-green-500/30'
                  : 'bg-surface-card/50'
              }`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {item.has_scan ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-600 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {item.subject && <span className="text-quest-primary">{item.subject}</span>}
                    {item.subject && item.lesson_number && ' '}
                    {item.lesson_number && <span className="text-gray-400">L.{item.lesson_number}</span>}
                    {!item.subject && !item.lesson_number && item.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{item.title}</p>
                </div>
              </div>

              {item.files.length > 0 && (
                <div className="flex items-center gap-1 ml-2">
                  {item.files.slice(0, 2).map((file) => (
                    <button
                      key={file.id}
                      onClick={(e) => handleDownload(e, file.id)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded transition-colors"
                      title={file.name}
                    >
                      <Download className="w-3 h-3" />
                      PDF
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-1 ml-2">
                {item.matched_scan_id ? (
                  <Link
                    to={`/parent/student/${studentId}/scans?edit=${item.matched_scan_id}`}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 rounded transition-colors"
                    title="Open related scan"
                  >
                    <Search className="w-3 h-3" />
                    Scan
                  </Link>
                ) : (
                  <Link
                    to="/parent/scans"
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                    title="Open scan workspace"
                  >
                    <Search className="w-3 h-3" />
                    Review
                  </Link>
                )}

                {item.course_id ? (
                  <Link
                    to={`/parent/courses/student/${studentId}/course/${item.course_id}`}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 rounded transition-colors"
                    title="Open course"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Course
                  </Link>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500 bg-surface-card/30 rounded-lg">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500/50" />
          <p className="text-sm">No homework tonight</p>
        </div>
      )}
    </div>
  )
}

export default TonightsHomework
